import React, { useMemo, useState } from "react";
import type { GenieCapabilityCategory } from "@/data/genie/categories";
import { SideDrawer } from '@/components/common/SideDrawer';
import { buildEmbedUrl } from "@/lib/embedUrl";
import { toast } from "sonner";
import QRCode from "qrcode.react";

interface CategoryActionsProps {
  category: GenieCapabilityCategory;
  onLiveAgent?: () => void;
}

const CategoryActions: React.FC<CategoryActionsProps> = ({ category, onLiveAgent }) => {
  const [openEmbed, setOpenEmbed] = useState(false);
  const [openQr, setOpenQr] = useState(false);

  const targets = useMemo(() => {
    const base = "https://helpgenie.ai";
    const slug = category.share?.slug;
    if (!slug) return null;
    return {
      liveUrl: `${base}/live-agent/${slug}`,
      embedUrl: buildEmbedUrl(base, slug),
      qrValue: `${base}/live-agent/${slug}`,
      phoneHref: category.share?.phone ? `tel:${category.share.phone}` : undefined,
      phoneDisplay: category.share?.phone,
    };
  }, [category.share]);

  const copy = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(message);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const formatPhone = (raw?: string) => {
    if (!raw) return "";
    const digits = raw.replace(/[^\d+]/g, "");
    const isUS = digits.startsWith("+1") && digits.length >= 12;
    if (isUS) {
      const rest = digits.replace("+1", "");
      const a = rest.slice(0, 3);
      const b = rest.slice(3, 6);
      const c = rest.slice(6, 10);
      return `+1 ${a}-${b}-${c}`;
    }
    return raw;
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 flex-1 w-full">
        {/* Live agent */}
        <div className="flex flex-col items-stretch md:items-center gap-1">
          <span className="px-1.5 py-[2px] rounded-full text-[11px] md:text-xs font-semibold text-[#1f2a44]" style={{ background: "linear-gradient(90deg, rgba(78,156,255,0.18) 0%, rgba(214,64,159,0.18) 100%)" }}>Click me</span>
          {targets ? (
            <a href={targets.liveUrl} target="_blank" rel="noreferrer" className="px-2.5 py-2 rounded-full border text-xs text-gray-700 bg-white hover:bg-gray-50 text-center w-full md:w-auto">Live agent link</a>
          ) : (
            <button type="button" onClick={onLiveAgent} className="px-2.5 py-2 rounded-full border text-xs text-gray-700 bg-white hover:bg-gray-50 w-full md:w-auto">Live agent link</button>
          )}
        </div>

        {/* QR code */}
        <div className="flex flex-col items-stretch md:items-center gap-1">
          <span className="px-1.5 py-[2px] rounded-full text-[11px] md:text-xs font-semibold text-[#1f2a44]" style={{ background: "linear-gradient(90deg, rgba(78,156,255,0.18) 0%, rgba(214,64,159,0.18) 100%)" }}>Scan me</span>
          <button type="button" onClick={() => setOpenQr(true)} className="px-2.5 py-2 rounded-full border text-xs text-gray-700 bg-white hover:bg-gray-50 w-full md:w-auto">Show QR</button>
        </div>

        {/* Embed */}
        <div className="flex flex-col items-stretch md:items-center gap-1">
          <span className="px-1.5 py-[2px] rounded-full text-[11px] md:text-xs font-semibold text-[#1f2a44]" style={{ background: "linear-gradient(90deg, rgba(78,156,255,0.18) 0%, rgba(214,64,159,0.18) 100%)" }}>Press me</span>
          <button type="button" onClick={() => setOpenEmbed(true)} className="px-2.5 py-2 rounded-full border text-xs text-gray-700 bg-white hover:bg-gray-50 w-full md:w-auto">{`< />`} Embed</button>
        </div>

        {/* Phone */}
        <div className="flex flex-col items-stretch md:items-center gap-1">
          <span className="px-1.5 py-[2px] rounded-full text-[11px] md:text-xs font-semibold text-[#1f2a44]" style={{ background: "linear-gradient(90deg, rgba(78,156,255,0.18) 0%, rgba(214,64,159,0.18) 100%)" }}>Call me</span>
          {targets?.phoneHref ? (
            <a href={targets.phoneHref} className="px-2.5 py-2 rounded-full border text-xs text-gray-700 bg-white hover:bg-gray-50 w-full md:w-auto tracking-wide">{formatPhone(targets.phoneDisplay)}</a>
          ) : (
            <div className="px-2.5 py-2 rounded-full border text-xs text-gray-400 bg-gray-50 w-full md:w-auto">Coming soon</div>
          )}
        </div>
      </div>

      {/* Embed Drawer shows live preview */}
      <SideDrawer isOpen={openEmbed} onClose={() => setOpenEmbed(false)} width="480px" title="Embed preview">
        <div className="py-4">
          {targets?.embedUrl ? (
            <div className="rounded-lg border overflow-hidden bg-white">
              <iframe
                src={targets.embedUrl}
                loading="lazy"
                width="100%"
                height="520"
                style={{ border: "0" }}
                allow="microphone"
                title="Genie Embed Preview"
              />
            </div>
          ) : (
            <div className="text-sm text-gray-500">No embed available</div>
          )}
        </div>
      </SideDrawer>

      {/* QR Drawer */}
      <SideDrawer isOpen={openQr} onClose={() => setOpenQr(false)} width="520px" title="Scan QR to open">
        <div className="flex flex-col items-center justify-center py-6">
          {targets?.qrValue ? (
            <>
              <QRCode value={targets.qrValue} size={280} />
              <div className="mt-4 text-sm text-blue-600 break-all text-center">{targets.qrValue}</div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => copy(targets.qrValue!, "Link copied")} className="px-3 py-1.5 text-xs rounded border">Copy link</button>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-500">No QR available</div>
          )}
        </div>
      </SideDrawer>
    </>
  );
};

export default CategoryActions;


